import React, { useRef, useState } from 'react';
import { useMutation, gql } from '@apollo/client';
import { withTranslation } from 'react-i18next';
// Only import the AddSystemConfig mutation string

import { AddSystemConfig } from '../../apollo';
import {
  Button,
  Box,
  Typography,
  Grid,
  TextField,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  CircularProgress // For upload loading indicator
} from '@mui/material';
import useStyles from './styles'; // Make sure this file exists
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import useGlobalStyles from '../../utils/globalStyles';
import AWS from 'aws-sdk';
import S3 from 'aws-sdk/clients/s3'; // Specific import for S3 client

// Parse GraphQL string into GQL object
const ADD_SYSTEM_CONFIG = gql`${AddSystemConfig}`;


function AddSystemConfigForm(props) { // Renamed component
  const { onClose, refetch } = props; // No 'config' prop needed for add-only
  const formRef = useRef();
  const fileInputRef = useRef(null);
  const classes = useStyles();
  const globalClasses = useGlobalStyles();
  const inputTextStyle = { color: 'black' };

  // This component is always in 'add' mode, so no 'isEditMode'
  const mutation = ADD_SYSTEM_CONFIG;

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { t } = props;

  // Initialize formData for adding a new config
  const [formData, setFormData] = useState({
    name: '',
    value: '',
    description: '',
    contents: '',
    isActive: false,
  });

  const [formErrors, setFormErrors] = useState({});
  const [openSnackbar, setOpenSnackbar] = useState(false);

  // State for file upload
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState(null);


  const allowedContentTypes = [
    'image/jpeg', 'image/png', 'application/pdf', 'video/mp4', 'video/quicktime',
    'audio/mpeg', 'audio/wav', 'text/plain', 'application/json',
  ];

  const getContentType = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    const contentTypesMap = {
      jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif',
      pdf: 'application/pdf', mp4: 'video/mp4', mov: 'video/quicktime',
      mp3: 'audio/mpeg', wav: 'audio/wav', txt: 'text/plain', json: 'application/json',
    };
    return contentTypesMap[extension] || 'application/octet-stream';
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const detectedType = getContentType(file.name);
      if (allowedContentTypes.includes(detectedType)) {
        setSelectedFile(file);
      } else {
        alert('Invalid file type. Please select a supported file (image, PDF, video, audio, text, JSON).');
        setSelectedFile(null);
      }
    }
  };

  const uploadFileToS3 = async () => {
    if (!selectedFile) {
      alert('No file selected for upload.');
      return;
    }

    setUploading(true);
    setError('');

    const S3_BUCKET = "nativenest"; // Replace with your bucket name
    const REGION = "ap-south-1";

    AWS.config.update({
      accessKeyId: process.env.NN_S3_RIDER_ACCESS_KEY_ID, // Using provided name
      secretAccessKey: process.env.NN_S3_RIDER_ACCESS_KEY_SECRET, // Using provided name
      region: REGION,
    });

    const folderStructure = process.env.REACT_APP_IMAGE_FOLDERSTRUCTURE || 'system_configs';
    const s3 = new S3();

    const params = {
      Bucket: S3_BUCKET,
      Key: `${folderStructure}/${selectedFile.name}`,
      Body: selectedFile,
      ContentType: getContentType(selectedFile.name),
      // ACL: 'public-read' // Uncomment if you need public read access
    };

    try {
      await s3.putObject(params).promise();
      const fileUrl = `https://${S3_BUCKET}.s3.${REGION}.amazonaws.com/${folderStructure}/${selectedFile.name}`;
      setUploadedFileName(selectedFile.name);
      setFormData(prev => ({
        ...prev,
        contents: fileUrl, // Set the S3 URL as the content
      }));
      setSuccess(t('File uploaded to S3 successfully!'));
      setOpenSnackbar(true);
    } catch (err) {
      setError(t('Error uploading file: ') + err.message);
      setOpenSnackbar(true);
      console.error("S3 Upload Error:", err);
    } finally {
      setUploading(false);
    }
  };


  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData({ ...formData, [name]: checked });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: '' });
    }
  };

  const validateFormData = () => {
    let errors = {};
    if (!formData.name) errors.name = t('Config Name is required');
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onCompleted = (data) => {
    setSuccess(t('System Config Added Successfully!'));
    setError('');
    setOpenSnackbar(true);
    refetch(); // Refetch data in the parent component
    setTimeout(() => {
      onClose(); // Close modal after success
    }, 1000);
  };

  const onError = ({ graphQLErrors, networkError }) => {
    setSuccess('');
    if (graphQLErrors) {
      // Replaced '?.message' with '&& .message'
      var errorMessage = graphQLErrors[0] && graphQLErrors[0].message || t('GraphQL error occurred.');
      setError(errorMessage);
    } else if (networkError) {
      setError(networkError.message || t('Network error occurred.'));
    } else {
      setError(t('An unknown error occurred.'));
    }
    setOpenSnackbar(true);
  };

  const [mutate, { loading: mutateLoading }] = useMutation(mutation, {
    onError,
    onCompleted,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateFormData()) {
      return;
    }

    const variables = {
      configInput: { // Always use configInput for adding
        name: formData.name,
        value: formData.value,
        description: formData.description,
        contents: formData.contents,
        isActive: formData.isActive,
      }
    };

    // Attempt to parse contents if it's expected to be JSON
    try {
      if (formData.contents && (formData.contents.startsWith('{') || formData.contents.startsWith('['))) {
        variables.configInput.contents = JSON.parse(formData.contents);
      }
    } catch (parseError) {
      setError(t('Invalid JSON format in Contents field.'));
      setOpenSnackbar(true);
      return;
    }

    mutate({ variables });
  };


  return (
    <Dialog
      open={true}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          onClose();
        }
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box className={classes.heading}>
          <Typography className={classes.textWhite}>
            {t('Add System Config')}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <form ref={formRef} onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                name="name"
                label={t('Config Name')}
                variant="outlined"
                fullWidth
                value={formData.name}
                onChange={handleInputChange}
                helperText={formErrors.name}
                error={Boolean(formErrors.name)}
                inputProps={{ style: inputTextStyle }}
                required // Required for new config
              />
              {/* <Typography variant="body2" component="span" color="textSecondary">
                {t('Current Value')}: {formData.name}
              </Typography> */}
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="value"
                label={t('Config Value')}
                variant="outlined"
                fullWidth
                value={formData.value}
                onChange={handleInputChange}
                inputProps={{ style: inputTextStyle }}
              />
              {/* <Typography variant="body2" component="span" color="textSecondary">
                {t('Current Value')}: {formData.value}
              </Typography> */}
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="description"
                label={t('Description')}
                variant="outlined"
                fullWidth
                multiline
                rows={3}
                value={formData.description}
                onChange={handleInputChange}
                inputProps={{ style: inputTextStyle }}
              />
              {/* <Typography variant="body2" component="span" color="textSecondary">
                {t('Current Value')}: {formData.description}
              </Typography> */}
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="contents"
                label={t('Contents (JSON or File URL)')}
                variant="outlined"
                fullWidth
                multiline
                rows={4}
                value={formData.contents}
                onChange={handleInputChange}
                inputProps={{ style: inputTextStyle }}
                placeholder={t('Enter JSON content or upload a file')}
              />
              {/* S3 File Upload Section */}
              <Box mt={2}>
                 <Typography variant="body2" component="span" color="textSecondary" mr={1}>
                    {t('Or upload a file:')}
                </Typography>
                <Button component="label" variant="outlined" className={globalClasses.dashbordBtn} startIcon={<CloudUploadIcon />}>
                  {t('Choose File')}
                  <input type="file" onChange={handleFileChange} ref={fileInputRef} style={{ display: 'none' }} />
                </Button>
                {selectedFile && (
                  <Typography variant="body2" ml={1}>
                    {t('Selected')}: {selectedFile.name}
                  </Typography>
                )}
                <Button
                  onClick={uploadFileToS3}
                  disabled={!selectedFile || uploading}
                  className={globalClasses.dashbordBtn}
                  startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                  sx={{ ml: 1 }}
                >
                  {uploading ? t('Uploading...') : t('Upload File')}
                </Button>
                {uploadedFileName && (
                    <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                        {t('Uploaded:')} {uploadedFileName}
                    </Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body1" component="span" style={{ color: formData.isActive ? 'gray' : 'red' }}>
                  {t('Is Active')}
                </Typography>
                <Switch name="isActive" checked={formData.isActive} onChange={handleSwitchChange} />
              </div>
            </Grid>
          </Grid>
          <DialogActions sx={{ pt: 3 }}>
            <Button
              type="submit"
              className={globalClasses.dashbordBtn}
              disabled={mutateLoading || uploading}
            >
              {t('Add')}
            </Button>
            <Button
              className={globalClasses.modalCancleBtn}
              onClick={onClose}
            >
              {t('Cancel')}
            </Button>
          </DialogActions>
        </form>
      </DialogContent>

      <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={() => setOpenSnackbar(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={error ? "error" : "success"} variant="filled" sx={{ width: '100%' }}>
          {error || success}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}

export default withTranslation()(AddSystemConfigForm);
